import React, { useState, useEffect, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import LayersIcon from "@material-ui/icons/Layers";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import AuthContext from "connections/auth/AuthProvider.js";
import {
  getMaskConfig,
  StartCalculations,
  getFilterOptions,
  getScenarioDetails,
  getModelsPremises,
} from "../../viewServices/calls/apiCalls";
import MaskRulesBuilder from "./MaskRulesBuilder";
import { ScenarioSelector } from "../../sharedComponents/ScenarioSelector";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import {
  grayColor,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react.js";

const useStyles = (clientColor) =>
  makeStyles((theme) => ({
    ...customSelectStyle(theme, clientColor),
    dialog: {
      "& .MuiDialog-paper": {
        minWidth: "750px",
        maxWidth: "950px",
      },
    },
    header: {
      borderBottom: `1px solid ${grayColor[8]}`,
      padding: theme.spacing(2.5),
      backgroundColor: grayColor[12],
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
    },
    headerTitle: {
      fontSize: "1.125rem",
      fontWeight: 500,
      margin: 0,
      color: clientColor,
    },
    headerIcon: {
      fontSize: "1.25rem",
      color: clientColor,
    },
    content: {
      padding: theme.spacing(3),
    },
    selectorControl: {
      marginBottom: theme.spacing(2.5),
      minWidth: "100%",
      "& > div": {
        "&:before": {
          borderBottomWidth: "1px !important",
          borderBottomColor: `${grayColor[4]}!important`,
        },
        "&:after": {
          borderBottomColor: `${clientColor}!important`,
        },
      },
      "& .MuiInputLabel-root": {
        "&.Mui-focused": {
          color: `${clientColor}!important`,
        },
      },
    },
    baseScenarioInfo: {
      border: `1px solid ${grayColor[8]}`,
      borderLeft: `4px solid ${clientColor}`,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
      backgroundColor: grayColor[12],
      marginBottom: theme.spacing(3),
      position: "relative",
      minHeight: "60px",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "60px",
    },
    infoRow: {
      display: "flex",
      gap: theme.spacing(4),
      marginBottom: theme.spacing(0.5),
    },
    infoLabel: {
      fontSize: "0.875rem",
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
    infoValue: {
      fontSize: "0.875rem",
      color: theme.palette.text.primary,
    },
    sectionTitle: {
      fontSize: "0.95rem",
      fontWeight: 500,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(2),
      color: clientColor,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    scenarioNameInput: {
      marginBottom: theme.spacing(3),
      "& > div": {
        "&:before": {
          borderBottomWidth: "1px !important",
          borderBottomColor: `${grayColor[4]}!important`,
        },
        "&:after": {
          borderBottomColor: `${clientColor}!important`,
        },
      },
      "& .MuiInputLabel-root": {
        "&.Mui-focused": {
          color: `${clientColor}!important`,
        },
      },
    },
    footer: {
      borderTop: `1px solid ${grayColor[8]}`,
      padding: theme.spacing(2),
      backgroundColor: grayColor[13],
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(1.5),
    },
    cancelButton: {
      minWidth: "100px",
      color: theme.palette.text.secondary,
      borderColor: grayColor[6],
      "&:hover": {
        borderColor: grayColor[4],
        backgroundColor: grayColor[13],
      },
    },
    applyButton: {
      minWidth: "120px",
      backgroundColor: clientColor,
      color: whiteColor,
      fontWeight: 500,
      "&:hover": {
        backgroundColor: clientColor,
        opacity: 0.9,
      },
      "&:disabled": {
        backgroundColor: grayColor[6],
        color: grayColor[4],
      },
    },
    settingsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gap: theme.spacing(2),
    },
  }));

const ScenarioMaskModal = ({
  open,
  onClose,
  baseScenario,
  scenarios = [],
  folders = [],
}) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";
  const classes = useStyles(clientColor)();
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.mask",
  });
  const { t: tCreation } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.scenarioCreation",
  });

  const [selectedBaseScenario, setSelectedBaseScenario] = useState(null);
  const [scenarioName, setScenarioName] = useState("");
  const [maskConfig, setMaskConfig] = useState(null);
  const [maskRules, setMaskRules] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Editable scenario settings state
  const [calculationMethod, setCalculationMethod] = useState("");
  const [pnlOptionUsed, setPnlOptionUsed] = useState("");
  const [routePremisesUsed, setRoutePremisesUsed] = useState("");
  const [modelsPremises, setModelsPremises] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Filter FINISHED scenarios from prop (real-time updates)
  const availableScenarios = useMemo(
    () => scenarios.filter((s) => s.status === "FINISHED"),
    [scenarios],
  );

  // Fetch mask configuration and model premises on mount
  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const [config, premisesData] = await Promise.all([
            getMaskConfig(),
            getModelsPremises(),
          ]);
          setMaskConfig(config);
          setModelsPremises(premisesData);
        } catch (error) {
          console.error("Failed to fetch data:", error);
          toast.error(t("errors.failed_load_config"));
        }
      };
      fetchData();
    }
  }, [open]);

  // Handle pre-selection or reset
  useEffect(() => {
    if (open) {
      setSelectedBaseScenario(baseScenario);
      setScenarioName("");
      setMaskRules([]);
      setFilterOptions({});
      setCalculationMethod("");
      setPnlOptionUsed("");
      setRoutePremisesUsed("");
    }
  }, [open, baseScenario]);

  // Fetch scenario details and filter options when base scenario changes
  useEffect(() => {
    if (selectedBaseScenario?.scenario_id) {
      const fetchScenarioData = async () => {
        setLoadingFilters(true);
        setLoadingSettings(true);
        try {
          console.log(
            "🎯 Fetching data for scenario:",
            selectedBaseScenario.scenario_id,
          );

          // Fetch both scenario details and filter options in parallel
          const [scenarioDetails, filterOpts] = await Promise.all([
            getScenarioDetails(selectedBaseScenario.scenario_id),
            getFilterOptions(selectedBaseScenario.scenario_id),
          ]);

          console.log("✅ Scenario details received:", scenarioDetails);
          console.log("✅ Filter options received:", filterOpts);

          // Set scenario settings from details
          setCalculationMethod(scenarioDetails.calculation_method || "");
          setPnlOptionUsed(scenarioDetails.pnl_option_used || "");
          setRoutePremisesUsed(scenarioDetails.route_premises_used || "");

          setFilterOptions(filterOpts);
        } catch (error) {
          console.error("❌ Failed to fetch scenario data:", error);
          setFilterOptions({});
        } finally {
          setLoadingFilters(false);
          setLoadingSettings(false);
        }
      };
      fetchScenarioData();
    } else {
      setFilterOptions({});
      setCalculationMethod("");
      setPnlOptionUsed("");
      setRoutePremisesUsed("");
    }
  }, [selectedBaseScenario?.scenario_id]);

  const handleSubmit = async () => {
    if (!scenarioName.trim()) {
      toast.error(t("errors.scenario_name_required"));
      return;
    }

    if (!selectedBaseScenario) {
      toast.error(t("errors.select_base_scenario"));
      return;
    }

    if (!maskRules || maskRules.length === 0) {
      toast.error(t("errors.at_least_one_rule"));
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        base_scenario_id: selectedBaseScenario.scenario_id,
        scenario_name: scenarioName.trim(),
        mask_rules: maskRules,
        // Include settings (can be used to override base scenario settings)
        calculation_method: calculationMethod,
        pnl_option_used: pnlOptionUsed,
        route_premises_used: routePremisesUsed,
      };

      console.log("🎭 Creating masked scenario with payload:", payload);

      const response = await StartCalculations(payload);

      if (response.status === "QUEUED") {
        toast.success(t("success.scenario_queued"));
        onClose(true); // Pass true to refresh scenarios
      } else {
        toast.error(t("errors.failed_create_scenario"));
      }
    } catch (error) {
      console.error("Failed to create masked scenario:", error);
      const errorMessage =
        typeof error === "string" ? error : t("errors.failed_create_scenario");
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      className={classes.dialog}
      disableEnforceFocus
    >
      {/* Header */}
      <Box className={classes.header}>
        <LayersIcon className={classes.headerIcon} />
        <Typography className={classes.headerTitle}>{t("title")}</Typography>
      </Box>

      {/* Content */}
      <DialogContent className={classes.content}>
        {/* Base Scenario Selection - Always Editable */}
        <Box mb={2.5}>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 8 }}
          >
            {t("select_base_scenario")} *
          </Typography>
          <ScenarioSelector
            scenarios={availableScenarios}
            folders={folders}
            value={selectedBaseScenario?.scenario_id || null}
            onChange={(scenarioId) => {
              const scenario = availableScenarios.find(
                (s) => s.scenario_id === scenarioId,
              );
              setSelectedBaseScenario(scenario);
            }}
            placeholder={t("select_base_scenario")}
            clientColor={clientColor}
            fullWidth
          />
        </Box>

        {/* Base Scenario Settings (if selected) */}
        {selectedBaseScenario && (
          <Box className={classes.baseScenarioInfo}>
            {loadingSettings ? (
              <Box className={classes.loadingContainer}>
                <ThreeDots
                  height="30"
                  width="30"
                  radius="6"
                  color={clientColor}
                  ariaLabel="three-dots-loading"
                  wrapperStyle={{}}
                  wrapperClassName=""
                  visible={true}
                />
              </Box>
            ) : (
              <Box className={classes.settingsGrid}>
                {/* Calculation Method */}
                <FormControl
                  size="small"
                  className={classes.selectFormControl}
                  disabled={loadingSettings}
                >
                  <InputLabel id="calculation-method-label">
                    {t("method")}
                  </InputLabel>
                  <Select
                    labelId="calculation-method-label"
                    value={calculationMethod}
                    onChange={(e) => setCalculationMethod(e.target.value)}
                    classes={{ select: classes.select }}
                    MenuProps={{
                      className: classes.selectMenu,
                      PaperProps: {
                        className: classes.selectPaper,
                        style: { maxHeight: 300 },
                      },
                    }}
                  >
                    <MenuItem
                      value="price_costbased"
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                    >
                      {tCreation("cost_based")}
                    </MenuItem>
                    <MenuItem
                      value="seek_sellout_price"
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                    >
                      {tCreation("value_based")}
                    </MenuItem>
                    <MenuItem
                      value="seek_nodes_margin"
                      classes={{
                        root: classes.selectMenuItem,
                        selected: classes.selectMenuItemSelected,
                      }}
                    >
                      {tCreation("chain_margin")}
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* PNL Option */}
                <FormControl
                  size="small"
                  className={classes.selectFormControl}
                  disabled={loadingSettings}
                >
                  <InputLabel id="pnl-option-label">
                    {t("pnl_premises")}
                  </InputLabel>
                  <Select
                    labelId="pnl-option-label"
                    value={pnlOptionUsed}
                    onChange={(e) => setPnlOptionUsed(e.target.value)}
                    classes={{ select: classes.select }}
                    MenuProps={{
                      className: classes.selectMenu,
                      PaperProps: {
                        className: classes.selectPaper,
                        style: { maxHeight: 300 },
                      },
                    }}
                  >
                    {modelsPremises?.options?.model02 &&
                      Object.entries(modelsPremises.options.model02).map(
                        ([key, label]) => (
                          <MenuItem
                            key={key}
                            value={key}
                            classes={{
                              root: classes.selectMenuItem,
                              selected: classes.selectMenuItemSelected,
                            }}
                          >
                            {label}
                          </MenuItem>
                        ),
                      )}
                  </Select>
                </FormControl>

                {/* Route Premises */}
                <FormControl
                  size="small"
                  className={classes.selectFormControl}
                  disabled={loadingSettings}
                >
                  <InputLabel id="route-premises-label">
                    {t("route_model")}
                  </InputLabel>
                  <Select
                    labelId="route-premises-label"
                    value={routePremisesUsed}
                    onChange={(e) => setRoutePremisesUsed(e.target.value)}
                    classes={{ select: classes.select }}
                    MenuProps={{
                      className: classes.selectMenu,
                      PaperProps: {
                        className: classes.selectPaper,
                        style: { maxHeight: 300 },
                      },
                    }}
                  >
                    {modelsPremises?.options?.model01 &&
                      Object.entries(modelsPremises.options.model01).map(
                        ([key, label]) => (
                          <MenuItem
                            key={key}
                            value={key}
                            classes={{
                              root: classes.selectMenuItem,
                              selected: classes.selectMenuItemSelected,
                            }}
                          >
                            {label}
                          </MenuItem>
                        ),
                      )}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        )}

        {/* Scenario Name Input */}
        <TextField
          fullWidth
          label={t("new_scenario_name")}
          value={scenarioName}
          onChange={(e) => setScenarioName(e.target.value)}
          required
          className={classes.scenarioNameInput}
          placeholder={t("scenario_name_placeholder")}
        />

        {/* Section Title */}
        <Typography className={classes.sectionTitle}>
          {t("adjustments_definition")}
        </Typography>

        {/* Mask Rules Builder */}
        <MaskRulesBuilder
          rules={maskRules}
          onChange={setMaskRules}
          config={maskConfig}
          filterOptions={filterOptions}
          loadingFilters={loadingFilters}
          scenarioId={selectedBaseScenario?.scenario_id}
          requireScenario={true}
        />
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions className={classes.footer}>
        <Button
          onClick={() => onClose(false)}
          variant="outlined"
          className={classes.cancelButton}
          disabled={isSubmitting}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          className={classes.applyButton}
          disabled={
            isSubmitting || !scenarioName.trim() || maskRules.length === 0
          }
        >
          {isSubmitting ? t("applying") : t("apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ScenarioMaskModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  baseScenario: PropTypes.shape({
    scenario_id: PropTypes.number,
    scenario_name: PropTypes.string,
    status: PropTypes.string,
  }),
  scenarios: PropTypes.array,
  folders: PropTypes.array,
};

export default ScenarioMaskModal;
